import { Box, Typography } from "@mui/joy";
import Add from "@mui/icons-material/Add";
import React, { useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import GenericTableView from "../../components/GenericTableView";
import { FilterMatchMode } from "primereact/api";
import { GenericTableColumTypes, GenericTableFilterTypes } from "../../enum/TableView";
import { v4 as uuid } from "uuid";
import { GenericTableColumns } from "../../models/GenericTableViewModel";
import { removeClient } from "../../service/clientService";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import { RouteComponentProps } from "../../models/RouteComponent";
import { removeEquipmentList } from "../../service/equipmentListService";
import {
  addRiskAssessment,
  addRiskJobTask,
  getRiskTaskReport,
  removeRiskAssessment,
  removeRiskJobTask,
  removeSiteRiskJobTask,
} from "../../service/riskAssessmentService";
import RiskJobTaskAdd from "./risk-job-task-Add";
import { Grid } from "@mui/material";
import { Button } from "primereact/button";
import { generatePdf } from "../../service/pdfGenerator";
import RiskJobTaskSiteAdd from "./risk-job-task-Site-Add";

export default function RiskJobTaskTable(props: RouteComponentProps) {
  const [selectedRecord, setRecord] = useState({} as any);
  const [visible, setVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [preview, setPreview] = useState("");
  const [reload, setReload] = useState(false);
  const toast = useRef(null as any);
  const dialog = useRef(null as any);

  const client_id = props?.session?.client_id;
  const site_id = props?.session?.site_id;

  const baseFilter = {
    type: { value: "", matchMode: FilterMatchMode.CONTAINS },
  };

  const deleteButton = (row: any) => {
    console.log("deleteButton row == ", selectedRecord);
    removeClient(selectedRecord.id);
  };

  const addButton = (row: any) => {
    console.log("addButton row == ", row);
    setRecord(null);
    setVisible(true);
  };

  const itemSelected = (row: any) => {
    console.log("itemSelected row == ", row);
    setRecord(row.value);
    editConfirm(row);
  };

  const contextItemSelected = (row: any) => {
    console.log("contextItemSelected row == ", row);
    setRecord(row.value);
  };

  const acceptFunc = async () => {
    props.setGlobalLoader(true);
    if (!site_id) {
      await removeRiskJobTask(selectedRecord.id);
    } else {
      await removeSiteRiskJobTask(selectedRecord.id);
    }
    props.setGlobalLoader(false);
    setReload(!reload);
    toast.current.show({
      severity: "success",
      summary: "Risk Task Deleted Successfully",
      life: 3000,
    });
  };

  const editConfirm = (row: any) => {
    setVisible(true);
  };

  const deleteConfirm = (row: any) => {
    confirmDialog({
      message: "Are you sure you want to proceed?",
      header: "Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => acceptFunc(),
    });
  };

  const unableToEdit = (row: any) => {
    toast.current.show({ severity: "warning", summary: "Unable To Edit Risk Task", life: 3000 });
  };

  const duplicate = (row: any) => {
    props.setGlobalLoader(true);
    addRiskJobTask({
      name: selectedRecord?.name,
      description: selectedRecord?.description,
      safe_work_method: selectedRecord?.safe_work_method,
      client_id: client_id,
    })
      .then((r: any) => {
        toast.current.show({
          severity: "success",
          summary: "Risk Task Created Successfully",
          life: 3000,
        });
        props.setGlobalLoader(false);
        setReload(!reload);
      })
      .catch(err => {
        toast.current.show({
          severity: "error",
          summary: "Failed to Create Risk Task",
          life: 3000,
        });
        props.setGlobalLoader(false);
      });
  };

  const download = async (row: any) => {
    props.setGlobalLoader(true);
    const content = await getRiskTaskReport(selectedRecord?.id);
    setPreview(content);
    setPreviewVisible(true);
    console.log("content == ", content);
    props.setGlobalLoader(false);
  };

  const generatePDF = async (row: any) => {
    props.setGlobalLoader(true);
    await generatePdf(preview, `Task_Step_Based_Risk_Assessment.pdf`);
    props.setGlobalLoader(false);
  };

  const contextItems = () => {
    let objArray: any[] = [];
    if (!site_id) {
      objArray.push({ label: "Duplicate", icon: "pi pi-fw pi-copy", command: duplicate });
      objArray.push({ label: "Edit", icon: "pi pi-fw pi-pencil", command: editConfirm });
    } else {
      objArray.push({ label: "Risk Assessment", icon: "pi pi-fw pi-clipboard", command: download });
    }
    objArray.push({ label: "Delete", icon: "pi pi-fw pi-trash", command: deleteConfirm });
    return objArray;
  };

  const headerElement = (
    <Grid container spacing={2}>
      <Grid item xs={7}>
        <label>Task Based Risk Assessment</label>
      </Grid>
      <Grid item xs={5}>
        <Button
          type="button"
          icon="pi pi-file-pdf"
          label="Generate PDF"
          className="p-button-outlined"
          size="small"
          style={{ float: "right", fontSize: "small", marginRight: 10, color: "#2dd36f" }}
          onClick={generatePDF}
        />
      </Grid>
    </Grid>
  );

  const columns: GenericTableColumns[] = [
    {
      field: "task_id",
      header: "Task ID",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: true,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "name",
      header: "Name",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "description",
      header: "Description",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "category_list",
      header: "Scope Based Task Categories",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
    {
      field: "safe_work_method_name",
      header: "Safe Work Method",
      type: GenericTableColumTypes.Text,
      visible: true,
      sort: false,
      filter: true,
      filterOption: {
        type: GenericTableFilterTypes.Text,
        options: [
          {
            label: "",
            value: "",
          },
        ],
      },
    },
  ];

  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "start", sm: "center" },
          flexWrap: "wrap",
          justifyContent: "space-between",
        }}
      >
        <Typography level="h2" component="h1">
          Risk Job Task
        </Typography>

        {/* <Button
            color="primary"
            startDecorator={<Add />}
            size="sm"
            onClick={() => setVisible(true)}
          >Add</Button> */}
      </Box>

      <GenericTableView
        reload={reload}
        baseFilters={baseFilter}
        url={site_id ? "site-job-task" : "risk-job-task"}
        columns={columns}
        contextItems={contextItems}
        addButton={addButton}
        itemSelect={itemSelected}
        contextItemSelected={contextItemSelected}
      />

      <Dialog
        ref={dialog}
        header={selectedRecord?.id ? "Update Task" : "Add Task"}
        visible={visible && !site_id}
        style={{ width: "50vw" }}
        onHide={() => setVisible(false)}
      >
        <RiskJobTaskAdd
          setGlobalLoading={props.setGlobalLoader}
          setReload={setReload}
          reload={reload}
          id={selectedRecord?.id}
          uuid={uuid()}
          details={selectedRecord}
          setVisible={setVisible}
          toast={toast}
          client={client_id}
        />
      </Dialog>

      <Dialog
        ref={dialog}
        header={"Add Job Tasks"}
        visible={visible && !!site_id}
        style={{ width: "85vw" }}
        onHide={() => setVisible(false)}
      >
        <RiskJobTaskSiteAdd
          setGlobalLoading={props.setGlobalLoader}
          setReload={setReload}
          reload={reload}
          id={selectedRecord?.id}
          uuid={uuid()}
          details={selectedRecord}
          setVisible={setVisible}
          toast={toast}
          client={client_id}
          site={site_id}
        />
      </Dialog>

      <Dialog
        header={headerElement}
        visible={previewVisible}
        style={{ width: "50vw" }}
        onHide={() => {
          if (!previewVisible) return;
          setPreviewVisible(false);
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: preview }} />
      </Dialog>

      <ConfirmDialog />

      <Toast ref={toast} />
    </React.Fragment>
  );
}
